/**
 * The lifecycle of a congress space: ONE definition, read by the admin, the
 * webapp and analytics. Pure date arithmetic on the fields every client already
 * receives, so each client derives it against its own `now` (a live countdown
 * ticks, and a persisted response must not freeze a state past its instant).
 *
 * What the countdown card DRAWS is not here: `deriveCongressCountdown` in the
 * webapp flattens several of these states for display and reads them from here.
 */

/** What the lifecycle depends on. The countdown window is not part of it. */
type CongressRunDates = { startDate: string | null; endDate: string | null };

type CongressDemotion = {
  featuredDaysAfterEnd: number | null;
  autoDemoteAfterWindow: boolean;
};

/**
 * Where a congress space sits in its lifecycle, independent of what the
 * countdown component draws. `evergreen` is a space without a start date or an
 * end date: it never moves through pre/live/post and must not be reported as a
 * congress that has ended. The countdown window plays no part here — a dated
 * space with no `countdownStartsAt` is `pre` before its start, like any other.
 */
export type CongressPhase = "evergreen" | "pre" | "live" | "post";

const DAY_MS = 86_400_000;

/**
 * The instant a congress is actually over — THE END DATE IS INCLUSIVE.
 *
 * `endDate` is the LAST DAY of the congress, not the moment it stops. The admin
 * picks a bare date (no time input, deliberately), so the stored value is
 * midnight at the START of that day. Comparing `now > endDate` therefore made
 * the whole final day count as `post`: on 9 September, the last day of Biocodex
 * EEC, the space would have dropped its Discovery banner while the stand was
 * still open.
 *
 * A DAY, not the reader's local end-of-day. That sounds less precise and is
 * more robust. The stored instant is local midnight IN THE ADMIN'S BROWSER AT
 * ENTRY, serialised to UTC — a date typed in Montreal lands on 04:00Z, one
 * typed in Paris on 22:00Z the day before. (The admin DOES call
 * `dayjs.tz.setDefault("Europe/Paris")`, which is easy to mistake for a pin:
 * it only changes what a bare `.tz()` means, never what the DatePicker
 * constructs. The admin's own `toParisISOString` exists precisely because the
 * picker yields browser-local values — and this field's `normalize` calls plain
 * `toISOString`, not that.) Snapping to "the end of that
 * instant's local calendar day" would ask each reader which calendar day the
 * instant falls on, and readers west of the entry zone answer with the day
 * BEFORE — losing the whole final day again, which is the bug. Adding 24h is
 * arithmetic on absolute instants: it cannot lose a day, and its residual error
 * is bounded by the entry offset.
 *
 * The real fix is upstream: store congress dates as plain calendar dates so the
 * entry timezone never enters. That is a migration, and it is registered.
 */
export function congressEndsAtMs(endDate: string): number {
  return new Date(endDate).getTime() + DAY_MS;
}

export function deriveCongressPhase(
  dates: CongressRunDates,
  now: Date,
): CongressPhase {
  if (dates.startDate === null || dates.endDate === null) return "evergreen";
  const nowMs = now.getTime();
  if (nowMs >= congressEndsAtMs(dates.endDate)) return "post";
  if (nowMs >= new Date(dates.startDate).getTime()) return "live";
  return "pre";
}

/**
 * The instant a congress space stops presenting as a congress and archives as
 * an ordinary section — the end of the POST-CONGRESS GRACE WINDOW the admin
 * authors as `featuredDaysAfterEnd`. Null when the space never demotes: no end
 * date, the admin turned the window off, or no window length.
 *
 * Measured from when the congress is OVER (`congressEndsAtMs`), so a one-day
 * shift there moves the demotion with it instead of leaving the window
 * anchored a day earlier than the congress it follows.
 *
 * The null window is a DELIBERATE EXCEPTION to fail-fast. The publish guard
 * refuses a space with `autoDemoteAfterWindow` and no `featuredDaysAfterEnd`, so
 * only rows published before that guard reach it. Throwing would take down the
 * room's Resources tab (and the admin's timeline) over a presentational number,
 * and archiving a client's space because a number is ABSENT is the surprising
 * direction. So it never demotes.
 */
export function congressDemotesAtMs(
  space: { endDate: string | null } & CongressDemotion,
): number | null {
  if (space.endDate === null) return null;
  if (!space.autoDemoteAfterWindow) return null;
  if (space.featuredDaysAfterEnd === null) return null;
  return congressEndsAtMs(space.endDate) + space.featuredDaysAfterEnd * DAY_MS;
}

/**
 * How a congress space presents itself in the room.
 *
 * `congress` is the space as it has always looked: the countdown card, its
 * description, and a calendar chip that sits ahead of the room's sections.
 * `section` is the same space, same id, same content, presenting as one of the
 * room's ordinary resources sections once its congress is well and truly over.
 *
 * The switch is `congressDemotesAtMs`, read by both the Discovery hero and the
 * room's Resources tab, so the two halves cannot disagree about whether a
 * congress is still current.
 *
 * NOT a second definition of "the congress is over": rule 1 asks
 * `deriveCongressPhase`, the same function that stamps `phase` on every
 * congress analytics event.
 */
export type CongressPresentation = "congress" | "section";

export function deriveCongressPresentation(
  space: CongressRunDates & CongressDemotion,
  now: Date,
): CongressPresentation {
  // 1. Anything but `post` is still a current congress — including `evergreen`,
  //    the dateless space that never moves through the lifecycle at all and so
  //    must never be archived by it.
  if (deriveCongressPhase(space, now) !== "post") return "congress";
  // 2. The grace window. No demotion instant (window off, or missing — see
  //    `congressDemotesAtMs`) keeps the congress look for as long as the space
  //    is published. `>=`, matching the phase boundary: both name the INSTANT
  //    the state flips.
  const demotesAtMs = congressDemotesAtMs(space);
  if (demotesAtMs === null) return "congress";
  return now.getTime() >= demotesAtMs ? "section" : "congress";
}
